import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, consulta } from '../api/cliente';
import type { Lead, Usuario } from '../api/tipos';
import { useSesion } from '../auth/sesion';
import {
  AreaTexto,
  Aviso,
  Campo,
  Cargando,
  Encabezado,
  Etiqueta,
  Seleccion,
  Tarjeta,
  Vacio,
  fechaHora,
} from '../ui/piezas';

const TIPOS_ANOTACION = [
  { valor: 'LLAMADA', texto: 'Llamada' },
  { valor: 'CORREO', texto: 'Correo' },
  { valor: 'WHATSAPP', texto: 'WhatsApp' },
  { valor: 'VISITA', texto: 'Visita' },
  { valor: 'NOTA', texto: 'Nota' },
];

export default function SolicitudFicha() {
  const { id } = useParams();
  const navegar = useNavigate();
  const cache = useQueryClient();
  const { puede } = useSesion();

  const [tipo, setTipo] = useState('LLAMADA');
  const [texto, setTexto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);

  const ficha = useQuery({
    queryKey: ['lead', id],
    queryFn: () => api<Lead>(`/admin/leads/${id}`),
  });

  const asesores = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api<Usuario[]>('/admin/usuarios'),
    enabled: puede('lead:asignar'),
  });

  const refrescar = () => void cache.invalidateQueries({ queryKey: ['lead', id] });

  const anotar = useMutation({
    mutationFn: () =>
      api<unknown>(`/admin/leads/${id}/interacciones`, {
        metodo: 'POST',
        cuerpo: { tipo, texto },
      }),
    onSuccess: () => {
      setTexto('');
      setError(null);
      setExito('Anotado.');
      refrescar();
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const mover = useMutation({
    mutationFn: (destino: string) =>
      api<Lead>(`/admin/leads/${id}/estado/${destino}${consulta({ motivo })}`, {
        metodo: 'POST',
      }),
    onSuccess: () => {
      setMotivo('');
      setError(null);
      setExito('Estado actualizado.');
      refrescar();
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const asignar = useMutation({
    mutationFn: (agenteId: string) =>
      api<Lead>(`/admin/leads/${id}/asignacion/${agenteId}`, { metodo: 'POST' }),
    onSuccess: () => {
      setError(null);
      setExito('Asignada.');
      refrescar();
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  if (ficha.isPending) return <Cargando />;
  const lead = ficha.data;
  if (!lead) return <Aviso error={ficha.error} />;

  // Cerrar como perdida o descartada exige motivo: sin él, el embudo se vuelve
  // una lista de números que no explica nada.
  const exigeMotivo = (destino: string) => destino === 'PERDIDO' || destino === 'DESCARTADO';

  return (
    <>
      <Encabezado titulo={lead.nombre} nota={lead.asunto}>
        <Etiqueta>{lead.estado}</Etiqueta>
        <button className="secundario" onClick={() => navegar('/solicitudes')}>
          Volver
        </button>
      </Encabezado>

      <Aviso error={error} exito={exito} />

      <Tarjeta titulo="Quién escribió">
        <div className="rejilla">
          <div>
            <label>Contacto</label>
            <div>{lead.contacto ?? '—'}</div>
          </div>
          <div>
            <label>Llegó de</label>
            <div>{lead.origen.replaceAll('_', ' ').toLowerCase()}</div>
          </div>
          <div>
            <label>Interés</label>
            <div>{lead.interes.toLowerCase()}</div>
          </div>
          <div>
            <label>Oficina</label>
            <div>{lead.sede}</div>
          </div>
          <div>
            <label>Asesor</label>
            <div>{lead.agente ?? 'sin asignar'}</div>
          </div>
          <div>
            <label>Autorización de datos</label>
            <div>
              {lead.consentimiento
                ? `${fechaHora(lead.consentimientoEn)} · v${lead.politicaVersion ?? '—'}`
                : 'no otorgada'}
            </div>
          </div>
        </div>

        {lead.mensaje && (
          <>
            <div style={{ height: 14 }} />
            <label>Mensaje</label>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{lead.mensaje}</p>
          </>
        )}
      </Tarjeta>

      {puede('lead:editar') && (
        <Tarjeta titulo="Mover en el embudo">
          <div className="fila">
            {lead.transicionesPosibles.map((destino) => (
              <button
                key={destino}
                className="secundario"
                disabled={mover.isPending || (exigeMotivo(destino) && !motivo.trim())}
                onClick={() => mover.mutate(destino)}
              >
                {destino.replaceAll('_', ' ').toLowerCase()}
              </button>
            ))}
            {lead.transicionesPosibles.length === 0 && (
              <span className="campo__ayuda">
                Ganada: lo que pase después es una venta, no una solicitud.
              </span>
            )}
          </div>

          {lead.transicionesPosibles.some(exigeMotivo) && (
            <>
              <div style={{ height: 12 }} />
              <Campo
                etiqueta="Motivo de cierre"
                ayuda="Obligatorio para perder o descartar. Sin él no se sabe si se pierde por precio, por zona o por demora"
                valor={motivo}
                alCambiar={setMotivo}
              />
            </>
          )}

          {puede('lead:asignar') && (
            <>
              <div style={{ height: 14 }} />
              <div style={{ maxWidth: 340 }}>
                <Seleccion
                  etiqueta="Asignar a"
                  ayuda="Tiene que ser de la misma oficina"
                  valor=""
                  alCambiar={(v) => v && asignar.mutate(v)}
                  vacio="Elige un asesor"
                  opciones={(asesores.data ?? [])
                    .filter((u) => u.estado === 'ACTIVO')
                    .map((u) => ({
                      valor: u.id,
                      texto: `${u.nombre} ${u.apellido} · ${u.sedeCodigo}`,
                    }))}
                />
              </div>
            </>
          )}
        </Tarjeta>
      )}

      {puede('lead:editar') && (
        <Tarjeta titulo="Anotar contacto">
          <div className="rejilla">
            <Seleccion etiqueta="Tipo" valor={tipo} alCambiar={setTipo} opciones={TIPOS_ANOTACION} />
          </div>
          <div style={{ height: 12 }} />
          <AreaTexto
            etiqueta="Qué pasó"
            valor={texto}
            alCambiar={setTexto}
            filas={3}
            ayuda="La primera anotación de una solicitud nueva la pasa a CONTACTADO sola"
          />
          <div className="fila fila--fin" style={{ marginTop: 12 }}>
            <button disabled={anotar.isPending || !texto.trim()} onClick={() => anotar.mutate()}>
              Anotar
            </button>
          </div>
        </Tarjeta>
      )}

      <Tarjeta titulo="Bitácora">
        {lead.interacciones.length === 0 ? (
          <Vacio>Todavía nadie ha anotado nada.</Vacio>
        ) : (
          <div className="tabla-marco">
            <table>
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>Tipo</th>
                  <th>Qué</th>
                  <th>Quién</th>
                </tr>
              </thead>
              <tbody>
                {lead.interacciones.map((i) => (
                  <tr key={i.id}>
                    <td>{fechaHora(i.ocurridaEn)}</td>
                    <td>{i.tipo.replaceAll('_', ' ').toLowerCase()}</td>
                    <td>{i.texto}</td>
                    <td>{i.usuario ?? 'sistema'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>
    </>
  );
}
